import React from 'react'
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Select,
  Spinner,
  Text
} from '@chakra-ui/react'
import startCase from 'lodash/startCase'
import { getEligibleUtilityTypes } from './helpers'
import type { OrphanedUtilityType } from './types'

type UtilityTypeSelectProps = {
  error: boolean
  fetching: boolean
  isDisabled: boolean
  onBlur: () => void
  onChange: (utilityTypeId: string) => void
  onRetry: () => void
  selectRef?: React.Ref<HTMLSelectElement>
  showValidationError: boolean
  utilityTypeId: string
  utilityTypes: OrphanedUtilityType[]
}

export default function UtilityTypeSelect({
  error,
  fetching,
  isDisabled,
  onBlur,
  onChange,
  onRetry,
  selectRef,
  showValidationError,
  utilityTypeId,
  utilityTypes
}: UtilityTypeSelectProps) {
  const eligibleUtilityTypes = getEligibleUtilityTypes(utilityTypes)

  if (fetching) {
    return (
      <HStack py={2}>
        <Spinner size='sm' />
        <Text fontSize='sm'>Loading utility types...</Text>
      </HStack>
    )
  }

  if (error) {
    return (
      <Alert status='error' gap={2}>
        <AlertIcon />
        <Text flex={1}>Unable to load utility types.</Text>
        <Button type='button' size='sm' variant='outline' onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    )
  }

  if (eligibleUtilityTypes.length === 0) {
    return (
      <Alert status='warning'>
        <AlertIcon />
        No utility types are available. Ask an administrator to configure one.
      </Alert>
    )
  }

  return (
    <FormControl
      isRequired
      isInvalid={showValidationError && !utilityTypeId}
      isDisabled={isDisabled}
    >
      <FormLabel>Utility type</FormLabel>
      <Select
        ref={selectRef}
        aria-label='Utility type'
        placeholder='Select a utility type'
        value={utilityTypeId}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
      >
        {eligibleUtilityTypes.map((utilityType) => (
          <option key={utilityType.id} value={utilityType.id}>
            {startCase(utilityType.name)}
          </option>
        ))}
      </Select>
      <FormErrorMessage>Select an eligible utility type.</FormErrorMessage>
    </FormControl>
  )
}
